import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Building, Globe, DollarSign, Clock, Landmark, MapPin, TrendingUp, Layers } from 'lucide-react';

const RealEstateFinancing = () => {
  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/93 via-slate-900/85 to-slate-900/65" />
        </div>
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-8">
              <Building className="h-4 w-4 text-amber-300" />
              <span className="text-xs font-semibold text-amber-200 uppercase tracking-wider">Real Estate Finance</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Real Estate<br /><span className="text-amber-300">Financing</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-6 max-w-xl">
              Financing real estate projects across the United States and the Caribbean. From $250K to $2B for refinances, new developments, structured deals, and everything in between.
            </p>
            <div className="flex items-center gap-4 mb-10">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-teal-400" />
                <span className="text-sm text-teal-300 font-medium">USA</span>
              </div>
              <div className="w-px h-4 bg-white/20" />
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-teal-400" />
                <span className="text-sm text-teal-300 font-medium">Caribbean</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/apply" data-testid="realestate-apply" className="inline-flex items-center gap-2 bg-amber-500 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-amber-400 transition-all">
                Start Your Project <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-all">Talk to Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: <DollarSign className="h-5 w-5 text-amber-400" />, value: '$250K to $2B', label: 'Project Size' },
              { icon: <Globe className="h-5 w-5 text-amber-400" />, value: 'USA + Caribbean', label: 'Markets Served' },
              { icon: <Building className="h-5 w-5 text-amber-400" />, value: 'All Asset Types', label: 'Commercial + Residential' },
              { icon: <Clock className="h-5 w-5 text-amber-400" />, value: 'Flexible', label: 'Custom Structures' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  {stat.icon}
                </div>
                <div>
                  <div className="text-base font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{stat.value}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Finance */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>What We Finance</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">Capital solutions for every stage of the real estate lifecycle, from acquisition through stabilization, across the U.S. and the Caribbean.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Landmark className="h-6 w-6" />,
                title: 'Refinances',
                desc: 'Replace existing debt with better terms, pull equity from stabilized assets, or restructure your capital stack to improve cash flow and free up capital for new opportunities.',
                color: 'bg-amber-50 text-amber-700',
              },
              {
                icon: <Building className="h-6 w-6" />,
                title: 'New Developments',
                desc: 'Ground up construction, conversions, and major renovations. We finance projects from pre-development through completion across residential, commercial, and mixed use.',
                color: 'bg-blue-50 text-blue-700',
              },
              {
                icon: <Layers className="h-6 w-6" />,
                title: 'Structured Deals',
                desc: 'Complex transactions that require creative capital solutions. Joint ventures, mezzanine financing, preferred equity, and custom structures tailored to your deal.',
                color: 'bg-purple-50 text-purple-700',
              },
              {
                icon: <TrendingUp className="h-6 w-6" />,
                title: 'Acquisitions',
                desc: 'Fast capital for property acquisitions. Single assets, portfolios, and entity level transactions with structures designed to close on your timeline.',
                color: 'bg-emerald-50 text-emerald-700',
              },
              {
                icon: <DollarSign className="h-6 w-6" />,
                title: 'Bridge Financing',
                desc: 'Short term capital to bridge the gap between acquisition and permanent financing, or to stabilize an asset before transitioning to long term debt.',
                color: 'bg-rose-50 text-rose-700',
              },
              {
                icon: <Globe className="h-6 w-6" />,
                title: 'Caribbean Projects',
                desc: 'Hospitality, resort, residential, and commercial developments across the Caribbean. We understand the unique dynamics of island market financing.',
                color: 'bg-teal-50 text-teal-700',
              },
            ].map((card) => (
              <div key={card.title} className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center mb-6`}>
                  {card.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{card.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Markets + Asset Types */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Markets */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Markets We Serve</h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                We finance real estate projects throughout the continental United States and across the Caribbean, bringing institutional level capital solutions to both domestic and island markets.
              </p>

              <div className="space-y-4">
                <div className="relative rounded-2xl overflow-hidden h-48">
                  <img src="https://images.pexels.com/photos/1209978/pexels-photo-1209978.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="US skyline" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-amber-400" />
                    <div>
                      <div className="text-base font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>United States</div>
                      <p className="text-xs text-slate-300">All 50 states. Commercial, residential, mixed use, industrial.</p>
                    </div>
                  </div>
                </div>

                <div className="relative rounded-2xl overflow-hidden h-48">
                  <img src="https://images.pexels.com/photos/1174732/pexels-photo-1174732.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Caribbean" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-teal-950/80 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center gap-3">
                    <Globe className="h-5 w-5 text-teal-400" />
                    <div>
                      <div className="text-base font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>Caribbean</div>
                      <p className="text-xs text-slate-300">Resort, hospitality, residential, and commercial developments.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Asset Types */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Asset Types</h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                We work across the full spectrum of real estate asset classes. Whether it's a single family portfolio, a Class A office building, or a Caribbean resort development, we structure capital to fit.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Multifamily',
                  'Office',
                  'Retail',
                  'Industrial',
                  'Hospitality',
                  'Mixed Use',
                  'Self Storage',
                  'Medical Office',
                  'Single Family Portfolios',
                  'Resort and Tourism',
                  'Senior Living',
                  'Land and Development',
                ].map((type) => (
                  <div key={type} className="flex items-center gap-2.5 p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                    <CheckCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700">{type}</span>
                  </div>
                ))}
              </div>

              <Link to="/apply" className="mt-8 block bg-slate-900 text-white rounded-2xl p-8 group hover:bg-slate-800 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Submit Your Project</h3>
                    <p className="text-sm text-slate-400">$250K to $2B. USA and Caribbean.</p>
                  </div>
                  <ArrowRight className="h-6 w-6 text-amber-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Quidity for Real Estate */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Why Quidity for Real Estate</h2>
            <p className="text-slate-500 max-w-xl mx-auto">We bring the same Cashflow Engineering discipline to real estate that we apply across all our financing solutions.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { value: '$250K', top: 'Starting at', bottom: 'Minimum project size' },
              { value: '$2B', top: 'Up to', bottom: 'Maximum project size' },
              { value: '2', top: 'Markets', bottom: 'USA + Caribbean' },
              { value: '12+', top: 'Asset Types', bottom: 'Full spectrum coverage' },
            ].map((stat) => (
              <div key={stat.value} className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">{stat.top}</div>
                <div className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{stat.value}</div>
                <div className="text-xs text-slate-500">{stat.bottom}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/1732414/pexels-photo-1732414.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/85" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Let's Build Something</h2>
          <p className="text-lg text-slate-400 mb-4 max-w-xl mx-auto">From a $250K refinance to a $2B development, we structure real estate capital that moves as fast as you do.</p>
          <p className="text-sm text-slate-500 mb-10 italic">Long term capital solutions designed to replace short term debt and improve cash flow.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/apply" className="inline-flex items-center gap-2 bg-amber-500 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-amber-400 transition-colors">Start Your Project <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RealEstateFinancing;
